import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Divider,
  Stack,
  Collapse,
  FormControl,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Tab,
  Tabs,
  // TextField,
  // InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
// import SearchIcon from "@mui/icons-material/Search";
import GenericHistoryTable from "./GenericHistoryTable";
import PatientJourney from "./PatientJourney";
import PatientHistoryDocumentsTab from "./PatientHistoryDocumentsTab";
import { PatientType } from ".";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { getAllHIstory, getAllergyData, getSocialHistoryData } from "../../EMR/services";
import dayjs from "dayjs";
import {
  formatFlexibleDate,
  parseFlexibleDate,
} from "../../utilis/component/NormalizeDate";
import {
  BillingDateGroupType,
  getBillingDateBalanceAmount,
  useFetchEncounterDetailsByEmr,
  useFetchBillingHistoryByEmrNo,
  useFetchPatientDetailsByEMR,
} from "../hookv2/apiHooks";
import moment from "moment/moment";
import { getPatientGroupingDisplay } from "../../utilis/methods/patientGrouping";
import PatientVipChip from "../../utilis/component/PatientVipChip";

const PATIENT_LEVEL_HISTORY_TYPES = new Set(["Allergy", "Social History"]);

const PatientDetailsHistory = () => {
  const { id, patientId } = useParams();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("");
  const [expandedDates, setExpandedDates] = useState<string[]>([]);
  const [showMore, setShowMore] = useState(false);
  const [mainTab, setMainTab] = useState(0);

  const {
    data: historyData,
    isLoading,
    mutate: fetchHistory,
  } = useMutation(getAllHIstory);
  const {
    data: allergyHistoryData,
    isLoading: isAllergyLoading,
    mutate: fetchAllergyHistory,
  } = useMutation(getAllergyData);
  const {
    data: socialHistoryData,
    isLoading: isSocialLoading,
    mutate: fetchSocialHistory,
  } = useMutation(getSocialHistoryData);
  const {
    mutate: fetchPatientDetails,
    data: patientData,
    isLoading: isPatientLoading,
  } = useFetchPatientDetailsByEMR({});
  const { mutate: fetchEncounterDates, data: encounterDateData } =
    useFetchEncounterDetailsByEmr();
  const {
    mutate: fetchBillingHistory,
    data: billingHistoryData,
    isLoading: isBillingLoading,
    reset: resetBillingHistory,
  } = useFetchBillingHistoryByEmrNo();

  useEffect(() => {
    const patientMasterId =
      Number(patientData?.response?.pmaster?.id) || Number(id) || 0;
    if (!patientMasterId) return;

    fetchHistory({ id: patientMasterId, tab: "all" });
    fetchAllergyHistory(String(patientMasterId));
    fetchSocialHistory(patientMasterId);
  }, [
    id,
    patientData?.response?.pmaster?.id,
    fetchHistory,
    fetchAllergyHistory,
    fetchSocialHistory,
  ]);

  const mergedHistoryData = useMemo(() => {
    const response = { ...(historyData?.response ?? {}) };

    if (
      Array.isArray(allergyHistoryData?.response) &&
      allergyHistoryData.response.length > 0
    ) {
      response.allergy = allergyHistoryData.response;
    }

    if (
      Array.isArray(socialHistoryData?.response) &&
      socialHistoryData.response.length > 0
    ) {
      response.socialHistory = socialHistoryData.response;
    }

    return historyData ? { ...historyData, response } : { response };
  }, [historyData, allergyHistoryData, socialHistoryData]);

  const isHistoryLoading = isLoading || isAllergyLoading || isSocialLoading;
  useEffect(() => {
    if (patientId) {
      fetchPatientDetails({ emrNo: patientId });
      fetchEncounterDates(patientId);
    }
  }, [patientId, fetchPatientDetails, fetchEncounterDates]);

  const handleBack = () => {
    navigate(-1);
  };

  const sections: { title: string; type: any; props: any[] }[] = [
    {
      title: "Vitals",
      type: "Vitals",
      props: [
        { heading: "Height", key: "height" },
        { heading: "Weight", key: "weight" },
        { heading: "Temperature", key: "temperature" },
        { heading: "BMI", key: "bmi" },
        { heading: "Systolic BP", key: "systolicBP" },
        { heading: "Diastolic BP", key: "diastolicBP" },
        { heading: "Sugar", key: "sugar" },
        { heading: "Pulse Rate", key: "heartRate" },
        { heading: "SPO2", key: "spo2" },
        { heading: "Respiratory", key: "respiratory" },
        { heading: "Pain Scale", key: "painscale" },
      ],
    },
    {
      title: "Complaints",
      type: "Complaints",
      props: [
        // { heading: "Date", key: "createdByDate", date: "DD-MM-YYYY" },
        { heading: "Complaint Name", key: "complaints.comName" },
        { heading: "HPI", key: "hpi.hpiName" },
        { heading: "Duration", key: "duration" },
        { heading: "Location", key: "location" },
        { heading: "Context", key: "context" },
        { heading: "Quality", key: "quality" },
        { heading: "Severity", key: "severity" },
        { heading: "Laterality", key: "laterality" },
        { heading: "Timing", key: "timing" },
        { heading: "Associated Symptoms", key: "associatedSymptoms" },
        { heading: "Disease Relation", key: "diseaseRelation" },
        { heading: "Anatomic Details", key: "anatomicDetails" },
      ],
    },
    {
      title: "Allergy",
      type: "Allergy",
      props: [
        { heading: "Allergy Type", key: "allergyType.allergyTypeName" },
        { heading: "Allergen", key: "allergen.allergenName" },
        { heading: "Severity", key: "severity.allergySeverityName" },
        { heading: "Status", key: "lifeCycleStatus.description" },
      ],
    },
    {
      title: "Medical History",
      type: "Medical History",
      props: [
        { heading: "Condition", key: "emrDisease.diseaseName" },
        { heading: "Medication", key: "medication" },
        { heading: "Duration", key: "duration" },
        { heading: "Status", key: "status" },
        {
          heading: "Date",
          key: "createdDate",
          date: "DD-MM-YYYY",
          format: "YYYY-MM-DDTHH:mm:ss.SSSZ",
        },
      ],
    },
    {
      title: "Social History",
      type: "Social History",
      props: [
        { heading: "Habit", key: "description.snomedDescription" },
        { heading: "Category", key: "category.name" },
        { heading: "Quantity", key: "quantity.quantityDesc" },
        { heading: "From", key: "fromDate", date: "DD-MM-YYYY" },
        { heading: "To", key: "toDate", date: "DD-MM-YYYY" },
        {
          heading: "Date",
          key: "createdDate",
          date: "DD-MM-YYYY",
          format: "YYYY-MM-DD HH:mm:ss.SSS",
        },
      ],
    },
    {
      title: "Family History",
      type: "Family History",
      props: [
        { heading: "Disease", key: "icd.icdName" },
        { heading: "Relation", key: "relation.description" },
        { heading: "Remarks", key: "notes" },
        {
          heading: "Date",
          key: "createdDate",
          date: "DD-MM-YYYY",
          format: "DD/MM/YYYY HH:mm:ss",
        },
      ],
    },
    {
      title: "Physical Examination",
      type: "Physical Examination",
      props: [
        { heading: "Description", key: "description" },
        { heading: "Date", key: "createdDate", date: "DD-MM-YYYY" },
      ],
    },
    {
      title: "Lab",
      type: "lab",
      props: [
        { heading: "Code", key: "labCptCode" },
        { heading: "Test Name", key: "labCptName" },
        { heading: "Qty", key: "labDetailsQty" },
        { heading: "Amount", key: "labDetailsAmt" },
        { heading: "P. Share", key: "labDetailsPshare" },
        { heading: "Date", key: "createdByDate", date: "DD-MM-YYYY" },
      ],
    },
    {
      title: "Radiology",
      type: "Radiology",
      props: [
        { heading: "Item", key: "cptDes" },
        { heading: "Unit Rate", key: "unitRate", numberType: "round" },
        { heading: "Qty", key: "radiologyDetailsQty" },
        { heading: "Amount", key: "radiologyDetailsAmt", numberType: "round" },
        {
          heading: "Date",
          key: "createdDate",
          date: "DD-MM-YYYY",
        },
      ],
    },
    {
      title: "Diagnosis",
      type: "Diagnosis",
      props: [
        { heading: "ICD Code", key: "icdCode.icdCode" },
        { heading: "ICD Description", key: "icdCode.icdDescription" },
        { heading: "Diagnosis Type", key: "diagnosisType" },
        { heading: "Diagnosis Date", key: "createdByDate", date: "DD-MM-YYYY" },
      ],
    },
    {
      title: "Problems",
      type: "Problems",
      props: [
        { heading: "ICD Code", key: "icdDto.icdCode" },
        { heading: "ICD Name", key: "icdDto.icdName" },
        { heading: "SNOMED", key: "icdSnomedDto.snomedDescription" },
        { heading: "Status", key: "lifeCycleStatusDto.description" },
        {
          heading: "Onset Date",
          key: "dateOfOnset",
          date: "DD-MM-YYYY",
          format: "DD-MM-YYYY HH:mm:ss",
        },
      ],
    },
    {
      title: "Procedures",
      type: "Procedures",
      props: [
        { heading: "CPT Code", key: "cptCode" },
        { heading: "CPT Name", key: "cptName" },
        { heading: "Qty", key: "quantity" },
        { heading: "Amount", key: "amount" },
        { heading: "P. Share", key: "pshare" },
        { heading: "Date", key: "createdByDate", date: "DD-MM-YYYY" },
      ],
    },
    {
      title: "Prescription",
      type: "Prescription",
      props: [
        { heading: "Package Name", key: "drug.drugPackageName" },
        { heading: "Duration", key: "prescriptionDuration" },
        { heading: "Quantity", key: "prescriptionQuantity" },
        { heading: "Instruction", key: "prescriptionRemarks" },
        { heading: "Refill", key: "refill" },
        {
          heading: "Start Date",
          key: "startDate",
          // date: "DD-MM-YYYY",
        },
      ],
    },
    {
      title: "Management Plan",
      type: "Management Plan",
      props: [
        {
          heading: "Date",
          key: "createdByDate",
          date: "DD-MM-YYYY",
        },
        { heading: "Management Plan", key: "managementPlan" },
      ],
    },
    {
      title: "Treatment Plan",
      type: "Treatment Plan",
      props: [
        { heading: "Health education", key: "healthEducation" },
        { heading: "Investigation", key: "investigation" },
        { heading: "Medication", key: "medication" },
        { heading: "Follow Up", key: "followUp" },
      ],
    },
    {
      title: "Clinical Impression",
      type: "clinical Impression",
      props: [
        { heading: "Clinical Impression", key: "clinicalImpression" },
      ],
    },
    // {
    //   title: "Upload Document",
    //   type: "Upload Document",
    //   props: [
    //     { heading: "Document Type", key: "documents" },
    //     { heading: "File", key: "uploadFile" },
    //     {
    //       heading: "Date",
    //       key: "createdDate",
    //       date: "DD-MM-YYYY",
    //     },
    //   ],
    // },
    {
      title: "Dental Procedure",
      type: "Dental Procedure",
      props: [
        { heading: "Tooth No", key: "tooth.0.toothNo" },
        { heading: "Procedure", key: "procedure.cptDetails.cptName" },
        { heading: "Qty", key: "procedure.quantity" },
        { heading: "Amount", key: "procedure.amount" },
        { heading: "P. Share", key: "procedure.pShare" },
        {
          heading: "Date",
          key: "procedure.createdDate",
          date: "DD-MM-YYYY",
          // API returns high-precision timestamps like `2026-05-06T22:55:45.58892909`
          // Moment will still parse it even if the format is not exact, but this helps.
          format: "YYYY-MM-DDTHH:mm:ss.SSSSSSSSS",
        },
      ],
    },
  ];
  const location = useLocation();
  const patient = location.state as PatientType | null;
  const fetchedPatient = patientData?.response;
  const fullName = fetchedPatient?.pmaster?.patientName
    ? fetchedPatient.pmaster.patientName
    : `${patient?.firstName || ""} ${patient?.middleName || ""} ${patient?.lastName || ""}`.trim();
  const dobForAge = fetchedPatient?.pmaster?.dob || patient?.dob || "";
  const rawAge = fetchedPatient?.pmaster?.age ?? patient?.age;
  const parsedRawAge =
    rawAge === null || rawAge === undefined || rawAge === ""
      ? null
      : Number(rawAge);
  const dobDate = parseFlexibleDate(dobForAge);
  const yearsFromDob =
    dobDate && dobDate.isValid() ? Math.max(dayjs().diff(dobDate, "year"), 0) : null;
  const monthsFromDob =
    dobDate && dobDate.isValid() ? Math.max(dayjs().diff(dobDate, "month"), 0) : null;
  const daysFromDob =
    dobDate && dobDate.isValid() ? Math.max(dayjs().diff(dobDate, "day"), 0) : null;

  const ageNumeric =
    typeof parsedRawAge === "number" &&
    !Number.isNaN(parsedRawAge) &&
    parsedRawAge > 0
      ? parsedRawAge
      : yearsFromDob || 0;

  const age =
    typeof parsedRawAge === "number" &&
    !Number.isNaN(parsedRawAge) &&
    parsedRawAge > 0
      ? `${parsedRawAge} Years`
      : typeof yearsFromDob === "number" && yearsFromDob > 0
        ? `${yearsFromDob} Years`
        : typeof monthsFromDob === "number" && monthsFromDob > 0
          ? `${monthsFromDob} Months`
          : typeof daysFromDob === "number" && daysFromDob >= 0
            ? `${daysFromDob} Days`
            : "-";
  const gender = fetchedPatient?.pmaster?.gender || patient?.gender || "-";
  const mobileNo =
    fetchedPatient?.amaster?.mobileNo || patient?.mobileNo || "-";
  const emrNo =
    fetchedPatient?.pmaster?.patientId ||
    patientId ||
    patient?.patientId ||
    "-";
  const profileImage = fetchedPatient?.image || "";
  const initials =
    `${String(fetchedPatient?.pmaster?.firstName || patient?.firstName || "")[0] || ""}${String(fetchedPatient?.pmaster?.lastName || patient?.lastName || "")[0] || ""}`.toUpperCase();
  const isInsurancePatient = Boolean(fetchedPatient?.insurancePatient);

  const extraDetails = [
    {
      label: "Grouping",
      value: getPatientGroupingDisplay(fetchedPatient) || "-",
    },
    { label: "Civil ID", value: fetchedPatient?.pmaster?.emiratesId || "-" },
    {
      label: "Passport No",
      value: fetchedPatient?.pmaster?.passportNum || "-",
    },
    { label: "GCC ID", value: fetchedPatient?.pmaster?.gccId || "-" },
    {
      label: "DOB",
      value: formatFlexibleDate(fetchedPatient?.pmaster?.dob),
    },
    { label: "Email", value: fetchedPatient?.amaster?.email || "-" },
    { label: "Address", value: fetchedPatient?.amaster?.address1 || "-" },
  ];

  const insuranceDetailRows = useMemo(() => {
    const ins = fetchedPatient?.insurancePatient;
    if (!ins) return [] as { label: string; value: string }[];
    return [
      { label: "Insurance provider", value: ins.insurance_name || "-" },
      { label: "Insurance type", value: ins.insurancetype_name || "-" },
      { label: "Insurance group", value: ins.inGroup_name || "-" },
      { label: "Plan", value: ins.inPlan_name || "-" },
      { label: "Policy no.", value: ins.policyNo || "-" },
      { label: "Card no.", value: ins.cardNo || "-" },
      { label: "ASOP No", value: ins.asopNo || "-" },
      { label: "Policy validity", value:ins.policyValidity ? moment(ins.policyValidity).format("DD-MM-YYYY") : "-" },
      {
        label: "Valid from",
        value:
          ins.policyValidityFrom &&
          dayjs(ins.policyValidityFrom).isValid()
            ? dayjs(ins.policyValidityFrom).format("DD-MM-YYYY")
            : ins.policyValidityFrom
              ? String(ins.policyValidityFrom)
              : "-",
      },
    ];
  }, [fetchedPatient?.insurancePatient]);

  const headerDetailItems = useMemo(() => {
    const base: { label: string; value: string }[] = [
      { label: "Age", value: age },
      { label: "Gender", value: gender },
      { label: "Phone", value: mobileNo },
      { label: "EMR", value: String(emrNo) },
      {
        label: "Grouping",
        value: getPatientGroupingDisplay(fetchedPatient) || "-",
      },
    ];
    const ins = fetchedPatient?.insurancePatient;
    if (ins) {
      base.push(
        { label: "Insurance", value: ins.insurance_name || "-" },
        { label: "Policy no.", value: ins.policyNo || "-" },
      );
    }
    return base;
  }, [age, gender, mobileNo, emrNo, fetchedPatient?.insurancePatient]);

  const encounterDateList = useMemo(() => {
    const list = Array.isArray(encounterDateData)
      ? encounterDateData
      : encounterDateData?.response || [];
    return list
      .map((item: any) => ({
        encounterId: Number(item?.encounterId),
        date: dayjs(item?.createdDate).isValid()
          ? dayjs(item.createdDate).format("YYYY-MM-DD")
          : "",
      }))
      .filter((item: { encounterId: number; date: string }) => item.date);
  }, [encounterDateData]);

  const availableDates = useMemo(() => {
    const uniqueByDate = new Set<string>();
    const mappedDates: string[] = [];

    encounterDateList.forEach((item: { date: string }) => {
      if (item.date && !uniqueByDate.has(item.date)) {
        uniqueByDate.add(item.date);
        mappedDates.push(item.date);
      }
    });

    return mappedDates;
  }, [encounterDateList]);

  const selectedEncounterId = useMemo(() => {
    if (!selectedDate) return null;
    const targetDate = selectedDate || availableDates[0];
    if (!targetDate) return null;
    const selectedEncounter = encounterDateList.find(
      (item: { encounterId: number; date: string }) => item.date === targetDate,
    );
    return selectedEncounter?.encounterId || null;
  }, [encounterDateList, selectedDate, availableDates]);

  const selectedEncounterIds = useMemo(() => {
    if (selectedDate) return [];
    return encounterDateList
      .map((item: { encounterId: number }) => Number(item.encounterId))
      .filter((id) => Number.isFinite(id) && id > 0);
  }, [selectedDate, encounterDateList]);

  const billingDatesToShow = useMemo(() => {
    const dates: BillingDateGroupType[] = billingHistoryData?.response?.dates || [];
    if (!selectedDate) return dates;

    // Some APIs include timestamped bill dates inside `paydetails.date`.
    // When filtering by a day, keep only matching bills and drop empty groups.
    return dates
      .map((group) => {
        const filteredPaydetails = (group.paydetails || []).filter(
          (bill) => dayjs(bill?.date).format("YYYY-MM-DD") === selectedDate,
        );
        return {
          ...group,
          paydetails: filteredPaydetails,
        };
      })
      .filter(
        (group) =>
          dayjs(group?.date).format("YYYY-MM-DD") === selectedDate ||
          group.paydetails.length > 0,
      );
  }, [billingHistoryData?.response?.dates, selectedDate]);

  const apiEncounterIdForDocuments = useMemo(() => {
    if (selectedEncounterId) return selectedEncounterId;
    const sorted = [...encounterDateList].sort(
      (a: { date: string }, b: { date: string }) =>
        dayjs(b.date).valueOf() - dayjs(a.date).valueOf(),
    );
    const top = sorted[0];
    return top?.encounterId && Number.isFinite(top.encounterId)
      ? top.encounterId
      : null;
  }, [selectedEncounterId, encounterDateList]);

  const patientMasterIdNum = Number(id) || 0;

  useEffect(() => {
    if (!selectedDate) return;
    if (!availableDates.includes(selectedDate)) {
      setSelectedDate("");
    }
  }, [availableDates, selectedDate]);

  useEffect(() => {
    resetBillingHistory();
  }, [emrNo, resetBillingHistory]);

  useEffect(() => {
    if (!emrNo || emrNo === "-") return;
    if (!billingHistoryData && !isBillingLoading) {
      fetchBillingHistory(String(emrNo));
    }
  }, [emrNo, billingHistoryData, isBillingLoading, fetchBillingHistory]);

  const historyDatesToRender = useMemo(() => {
    if (selectedDate) return [selectedDate];
    if (availableDates.length) return availableDates;
    return [""];
  }, [selectedDate, availableDates]);

  const renderHistorySections = (filterDate: string, keyPrefix: string) => {
    const encounterIdForDate = filterDate
      ? encounterDateList.find(
          (item: { encounterId: number; date: string }) =>
            item.date === filterDate,
        )?.encounterId ?? null
      : null;

    return (
    <Grid container spacing={2}>
      {sections.map((section, index) => (
        <Grid item xs={12} md={6} key={`${keyPrefix}-${index}`}>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 5,
                height: 18,
                bgcolor: "primary.main",
                borderRadius: "4px",
              }}
            />
            <Typography variant="subtitle2" fontWeight="800" color="text.primary">
              {section.title}
            </Typography>
          </Box>
          <GenericHistoryTable
            title={section.type}
            tableprops={section.props}
            maxHeight={220}
            data={mergedHistoryData}
            isLoading={isHistoryLoading}
            filterDate={filterDate}
            filterEncounterId={encounterIdForDate}
            patientLevel={PATIENT_LEVEL_HISTORY_TYPES.has(section.type)}
          />
        </Grid>
      ))}
    </Grid>
  );
  };

  return (
    <Box p={3} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
        <Grid container alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                borderColor: "#e2e8f0",
                color: "#475569",
                bgcolor: "white",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "rgba(25, 118, 210, 0.04)",
                },
              }}
            >
              Back
            </Button>
          </Grid>
          <Grid item xs>
            <Typography
              variant="h5"
              fontWeight="800"
              color="primary.main"
              sx={{ letterSpacing: "-0.5px" }}
            >
              Patient History
            </Typography>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2.5,
            borderRadius: "12px",
            bgcolor: "#ffffff",
            border: "1px solid #eef2f6",
            boxShadow:
              "0 4px 6px -1px rgba(0,0,0,0.01), 0 2px 4px -1px rgba(0,0,0,0.006)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Avatar
                src={profileImage}
                alt="Patient image"
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: "10px",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                }}
              >
                {initials || "P"}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: "0.6rem",
                    lineHeight: 1.1,
                  }}
                >
                  Name
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    color="text.primary"
                    sx={{
                      fontSize: "0.82rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: { xs: "100%", md: 200 },
                    }}
                  >
                    {isPatientLoading ? "Loading..." : fullName || "-"}
                  </Typography>
                  <PatientVipChip isVip={fetchedPatient?.pmaster?.isVip} />
                </Box>
              </Box>
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                height: 34,
                alignSelf: "center",
                display: { xs: "none", md: "block" },
              }}
            />

            <Grid container spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              {headerDetailItems.map((item) => (
                <Grid item xs={6} sm={3} key={item.label}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.6rem",
                      lineHeight: 1.1,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    color="text.primary"
                    sx={{ fontSize: "0.8rem", wordBreak: "break-word" }}
                  >
                    {item.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                ml: { md: "auto" },
                alignSelf: { xs: "flex-end", md: "center" },
              }}
            >
              <Button
                size="small"
                variant="text"
                onClick={() => setShowMore((prev) => !prev)}
                endIcon={showMore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  minWidth: 92,
                  px: 1,
                  py: 0.4,
                  fontSize: "0.72rem",
                  borderRadius: "8px",
                }}
              >
                {showMore ? "Show less" : "Show more"}
              </Button>
            </Box>
          </Stack>

          <Collapse in={showMore} timeout="auto" unmountOnExit>
            <Divider sx={{ my: 1.25 }} />
            <Grid container spacing={1} sx={{ mt: 0 }}>
              {extraDetails.map((detail) => (
                <Grid item xs={6} md={4} key={detail.label}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.58rem",
                      lineHeight: 1.1,
                    }}
                  >
                    {detail.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    color="text.primary"
                    sx={{ fontSize: "0.77rem", wordBreak: "break-word" }}
                  >
                    {detail.value}
                  </Typography>
                </Grid>
              ))}
              {insuranceDetailRows.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        textTransform: "uppercase",
                        fontSize: "0.58rem",
                        letterSpacing: "0.04em",
                        color: "primary.main",
                      }}
                    >
                      Insurance details
                    </Typography>
                  </Grid>
                  {insuranceDetailRows.map((detail) => (
                    <Grid item xs={6} md={4} key={`ins-${detail.label}`}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: "0.58rem",
                          lineHeight: 1.1,
                        }}
                      >
                        {detail.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        color="text.primary"
                        sx={{ fontSize: "0.77rem", wordBreak: "break-word" }}
                      >
                        {detail.value}
                      </Typography>
                    </Grid>
                  ))}
                </>
              )}
              {/* <Grid item xs={6} md={4}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: "0.58rem",
                    lineHeight: 1.1,
                  }}
                >
                  Master ID
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color="text.primary"
                  sx={{ fontSize: "0.77rem" }}
                >
                  {id || "-"}
                </Typography>
              </Grid> */}
            </Grid>
          </Collapse>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            mb: 2,
            borderRadius: "12px",
            bgcolor: "#ffffff",
            border: "1px solid #eef2f6",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: "text.secondary", ml: 0.5 }}
          >
            DATE:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={selectedDate}
              displayEmpty
              onChange={(e) => setSelectedDate(String(e.target.value))}
              sx={{ fontSize: "0.82rem", height: 34 }}
            >
              <MenuItem value="">All dates</MenuItem>
              {!availableDates.length && <MenuItem value="">No date available</MenuItem>}
              {availableDates.map((date) => (
                <MenuItem key={date} value={date}>
                  {dayjs(date).format("DD-MM-YYYY")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mb: 2,
            borderRadius: "12px",
            bgcolor: "#ffffff",
            border: "1px solid #eef2f6",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={mainTab}
            onChange={(_, v) => setMainTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 1,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
            }}
          >
            <Tab label="Encounter details" />
            <Tab label="Billing history" />
            <Tab label="Patient journey" />
            <Tab label="Documents" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {mainTab === 0 && (
              <>
                {!availableDates.length && !isHistoryLoading && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: "12px",
                      border: "1px solid #eef2f6",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No encounter dates found. Showing all available history.
                    </Typography>
                  </Paper>
                )}

                {historyDatesToRender.map((date) => (
                    <Accordion
                      key={date || "all-history"}
                      disableGutters
                      elevation={0}
                      expanded={
                        selectedDate || !availableDates.length
                          ? true
                          : expandedDates.includes(date)
                      }
                      onChange={() => {
                        if (selectedDate || !availableDates.length) return;
                        setExpandedDates((prev) =>
                          prev.includes(date)
                            ? prev.filter(
                                (expandedDate) => expandedDate !== date,
                              )
                            : [...prev, date],
                        );
                      }}
                      sx={{
                        mb: 1.5,
                        border: "1px solid #e5eaf0",
                        borderRadius: "10px !important",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography
                          sx={{ fontWeight: 700, fontSize: "0.86rem" }}
                        >
                          {date
                            ? dayjs(date).format("DD-MM-YYYY")
                            : "All history"}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0.5 }}>
                        {renderHistorySections(date, `selected-${date || "all"}`)}
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </>
            )}

            {mainTab === 1 && (
              <Box>
                {isBillingLoading ? (
                  <Box
                    sx={{
                      py: 5,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : billingDatesToShow.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No billing history found
                    {selectedDate
                      ? ` for ${dayjs(selectedDate).format("DD-MM-YYYY")}.`
                      : "."}
                  </Typography>
                ) : (
                  <>
                    {/*
                      When a date is selected, show the per-date totals (from `dates[]`).
                      Otherwise, show the overall grand totals (from `response`).
                    */}
                    {selectedDate && billingDatesToShow.length > 0 ? (
                      <Paper
                        elevation={0}
                        sx={{
                          mb: 2,
                          p: 1.25,
                          borderRadius: "12px",
                          border: "1px solid #eef2f6",
                          bgcolor: "#fbfdff",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={800}
                          sx={{ mb: 1 }}
                        >
                          {dayjs(billingDatesToShow[0]?.date).format("DD-MM-YYYY")}
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Total CPT
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {Number(
                                billingDatesToShow[0]?.totalCptAmount ?? 0,
                              ).toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Patient Share
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {Number(
                                billingDatesToShow[0]?.patientShareAmount ?? 0,
                              ).toFixed(2)}
                            </Typography>
                          </Grid>
                          {isInsurancePatient ? (
                            <Grid item xs={6} md={3}>
                              <Typography variant="caption" color="text.secondary">
                                Insurance Amount
                              </Typography>
                              <Typography variant="body2" fontWeight={700}>
                                {Number(
                                  billingDatesToShow[0]?.totalInsuranceAmount ?? 0,
                                ).toFixed(2)}
                              </Typography>
                            </Grid>
                          ) : null}
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Amount Paid
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {Number(
                                billingDatesToShow[0]?.totalAmountPayed ?? 0,
                              ).toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Total discount amount
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {Number(
                                Number(billingDatesToShow[0]?.totalDiscountAmount ?? 0) +
                                  Number(
                                    billingDatesToShow[0]?.totalItemWiseDiscount ?? 0,
                                  ),
                              ).toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        mb: 2,
                        p: 1.25,
                        borderRadius: "12px",
                        border: "1px solid #eef2f6",
                        bgcolor: "#fbfdff",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={800}
                        sx={{ mb: 1 }}
                      >
                        Grand total
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Total CPT
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {Number(
                              billingHistoryData?.response?.totalCptAmount ?? 0,
                            ).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Grand total patient share
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {Number(
                              billingHistoryData?.response?.grantTotalPatShare ??
                                0,
                            ).toFixed(2)}
                          </Typography>
                        </Grid>
                        {isInsurancePatient ? (
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Grand total insurance amount
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {Number(
                                billingHistoryData?.response
                                  ?.grantTotalInsuranceAmount ?? 0,
                              ).toFixed(2)}
                            </Typography>
                          </Grid>
                        ) : null}
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Grand total discount amount
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {Number(
                              (billingHistoryData?.response
                                ?.grandTotalItemWiseDiscount ?? 0) +
                                (billingHistoryData?.response
                                  ?.grandTotalDiscountamount ?? 0),
                            ).toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                    )}

                    {billingDatesToShow.map((billingDateData) => {

                    return (
                      <Box
                        key={String(billingDateData.date)}
                        sx={{ mb: 3 }}
                      >
                      <Typography
                        variant="subtitle2"
                        fontWeight={800}
                        color="primary.main"
                        sx={{ mb: 1.5 }}
                      >
                        {dayjs(billingDateData.date).format("DD-MM-YYYY")}
                      </Typography>
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Total CPT
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {Number(
                              billingDateData.totalCptAmount || 0,
                            ).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Patient Share
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {Number(
                              billingDateData.patientShareAmount 
                            ).toFixed(2)}
                          </Typography>
                        </Grid>
                        {/* <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Patient Share
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {Number(
                              billingDateData.patientShareAmount).toFixed(2)}
                          </Typography>
                        </Grid> */}
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Balance Amount
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {getBillingDateBalanceAmount(
                              billingDateData,
                            ).toFixed(2)}
                          </Typography>
                        </Grid>
                        {isInsurancePatient ? (
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Insurance Amount
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {Number(
                                billingDateData.totalInsuranceAmount || 0,
                              ).toFixed(2)}
                            </Typography>
                          </Grid>
                        ) : null}
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Amount Paid
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {Number(
                              billingDateData.totalAmountPayed || 0,
                            ).toFixed(2)}
                          </Typography>
                        </Grid>
                        {/* <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Total discount amount
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {Number(
                              Number(billingDateData.totalDiscountAmount ?? 0) +
                                Number(billingDateData.totalItemWiseDiscount ?? 0),
                            ).toFixed(2)}
                          </Typography>
                        </Grid> */}
                      </Grid>

                      {billingDateData.paydetails?.map((bill) => (
                        <Paper
                          key={`${bill.billNo}-${bill.encounterId}-${billingDateData.date}`}
                          elevation={0}
                          sx={{
                            mb: 1.5,
                            p: 1.25,
                            border: "1px solid #eef2f6",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ mb: 0.6 }}
                          >
                            Bill #{bill.billNo} - {bill.doctorName || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Encounter: {bill.encounterId} | Date:{" "}
                            {bill.date
                              ? dayjs(bill.date).format("DD-MM-YYYY")
                              : "-"}
                          </Typography>
                          <Box sx={{ mt: 0.8 }}>
                            {(bill.allBillsByResponseDto || []).map((item) => (
                              <Box
                                key={item.billingLineId}
                                sx={{
                                  py: 0.8,
                                  borderBottom: "1px dashed #edf0f3",
                                  "&:last-of-type": {
                                    borderBottom: "none",
                                  },
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {item.itemCode} - {item.itemName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Qty: {item.quantity} | Unit Rate: {item.rate} |
                                  Total: {item.totalAmount} | Actual Pat Share:{" "}
                                  {(() => {
                                    const actualPatShare = Number(
                                      (item as any).actualPatshare ??
                                        item.patShare ??
                                        0,
                                    );
                                    const discountedPatShare = Number(
                                      (item as any).discountedPatshare ??
                                        (item as any).discountedPatShare ??
                                        actualPatShare,
                                    );
                                    const discountDisplay = String(
                                      (item as any).discountDisplay ?? "",
                                    ).trim();

                                    const hasDiscount =
                                      discountedPatShare !== actualPatShare ||
                                      (discountDisplay.length > 0 &&
                                        discountDisplay !== "0 amt" &&
                                        discountDisplay !== "0");

                                    return (
                                      <>
                                        {actualPatShare.toFixed(2)}
                                        {isInsurancePatient ? (
                                          <>
                                            {" "}
                                            | Insurance Amt:{" "}
                                            {Number(item.insAmount || 0).toFixed(
                                              2,
                                            )}
                                          </>
                                        ) : null}
                                        {hasDiscount ? (
                                          <>
                                            {" "}
                                            | Discount:{" "}
                                            {discountDisplay || "-"} | Discounted
                                            Pat Share:{" "}
                                            {item.patShare.toFixed(2)}
                                          </>
                                        ) : null}
                                      </>
                                    );
                                  })()}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                    );
                  })}
                  </>
                )}
              </Box>
            )}

            {mainTab === 2 && (
              <PatientJourney
                encounterId={selectedEncounterId}
                encounterIds={selectedEncounterIds}
                patient={
                  patient || {
                    id: Number(id || 0),
                    status: 1,
                    encounterId:
                      fetchedPatient?.patientDetails?.encounterId || null,
                    emiratesId: fetchedPatient?.pmaster?.emiratesId || "",
                    gender: String(gender),
                    age: ageNumeric,
                    patientId: String(emrNo),
                    mobileNo: String(mobileNo),
                    firstName: fetchedPatient?.pmaster?.firstName || "",
                    middleName: fetchedPatient?.pmaster?.middleName || "",
                    lastName: fetchedPatient?.pmaster?.lastName || "",
                    dob: fetchedPatient?.pmaster?.dob || "",
                    foDate: fetchedPatient?.pmaster?.foDate || "",
                    isVip: fetchedPatient?.pmaster?.isVip || 0,
                  }
                }
              />
            )}

            {mainTab === 3 && (
              <PatientHistoryDocumentsTab
                patientMasterId={patientMasterIdNum}
                apiEncounterId={apiEncounterIdForDocuments}
                selectedEncounterId={selectedEncounterId}
                insurancePatient={
                  fetchedPatient?.insurancePatient ?? null
                }
              />
            )}
          </Box>
        </Paper>
      </Box>
  );
};

export default PatientDetailsHistory;
